/**
 * React Query hook for POST /api/analysis/documentInsights.
 *
 * Implemented as useQuery (not useMutation) because we need both:
 *   - automatic deduplication of concurrent fetches (React 18 StrictMode safety)
 *   - an automatic cache subscription so the UI re-renders the moment the
 *     response lands
 *
 * The user's "Refresh" action sets a force-flag that the next queryFn call
 * forwards as `force_refresh: true`. Otherwise the call uses the standard
 * server-side cache path.
 *
 * Cold requests can take 25–50 s; warm/cached requests return under 5 s.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../lib/api';
import { LOADING_STAGES } from './types';
import type {
  CommentarySection,
  ConfidenceType,
  DocumentInsightsData,
  DocumentInsightsRequest,
  DocumentInsightsResponse,
  DocumentResult,
  OverallSummary,
  VerdictType,
} from './types';

// ─── Raw API wire types ───────────────────────────────────────────────────────
// The backend shape differs from our canonical component types in several ways:
//   - top-level company name lives under "company" not "company_name"
//   - commentary objects are wrapped inside a "performance" sub-key
//   - recommendations can be a string instead of string[]
//   - extraction_confidence can be a numeric 0-1 float

interface RawPerformance {
  title?: string;
  summary?: string;
  key_points?: string[];
  verdict?: string;
}

interface RawCommentary {
  metric_name?: string;
  performance?: RawPerformance;
  // flat shape (older/alternate format)
  title?: string;
  summary?: string;
  key_points?: string[];
  verdict?: string;
}

interface RawOverallSummary {
  executive_summary?: string;
  key_highlights?: string[];
  recommendations?: string | string[];
  risk_factors?: string[];
}

interface RawInsights {
  overall_summary?: RawOverallSummary;
  revenue_commentary?: RawCommentary;
  ebitda_commentary?: RawCommentary;
  asset_efficiency_commentary?: RawCommentary;
  industrial_performance_commentary?: RawCommentary;
  financial_metrics?: Record<string, string>;
  sentiment?: string;
  extraction_confidence?: number | string;
}

interface RawDocumentResult {
  status?: string;
  message?: string;
  document_type?: string;
  metadata?: {
    pdf_url?: string;
    document_type?: string;
    fiscal_year?: string | null;
    fiscal_quarter?: string | null;
    source?: string;
    download_status?: string;
    extraction_timestamp?: string;
    saved_path?: string | null;
  };
  insights?: RawInsights;
}

interface RawApiResponse {
  status?: string;
  company?: string;
  company_name?: string;
  input_company_name?: string;
  combined_summary?: string;
  message?: string;
  analysis_date?: string;
  mda?: RawDocumentResult;
  earnings?: RawDocumentResult;
}

// ─── Normalizers ──────────────────────────────────────────────────────────────

function normalizeVerdict(verdict?: string): VerdictType {
  if (!verdict) return 'Neutral';
  const v = verdict.toLowerCase();
  if (v === 'positive') return 'Positive';
  if (v === 'negative') return 'Negative';
  if (v === 'neutral') return 'Neutral';
  if (v === 'mixed') return 'Mixed';
  // Infer from free-text verdicts
  const negative = ['challeng', 'pressure', 'risk', 'declin', 'impact', 'volatile', 'weak', 'concern'];
  const positive = ['strong', 'improv', 'positive', 'growth', 'robust', 'opportun', 'effective', 'success'];
  const hasMixed = negative.some((w) => v.includes(w)) && positive.some((w) => v.includes(w));
  if (hasMixed) return 'Mixed';
  if (positive.some((w) => v.includes(w))) return 'Positive';
  if (negative.some((w) => v.includes(w))) return 'Mixed';
  return 'Neutral';
}

function normalizeConfidence(raw?: number | string): ConfidenceType | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'string') {
    const s = raw.toLowerCase();
    if (s === 'high') return 'High';
    if (s === 'medium') return 'Medium';
    if (s === 'low') return 'Low';
    return undefined;
  }
  // Numeric 0–1 float
  if (raw >= 0.8) return 'High';
  if (raw >= 0.5) return 'Medium';
  return 'Low';
}

function normalizeCommentary(raw?: RawCommentary): CommentarySection | undefined {
  if (!raw) return undefined;
  // Accept both nested (performance key) and flat formats
  const perf: RawPerformance = raw.performance ?? raw;
  const title = perf.title ?? raw.metric_name ?? '';
  if (!title && !perf.summary) return undefined;
  return {
    title,
    summary: perf.summary ?? '',
    key_points: Array.isArray(perf.key_points) ? perf.key_points : [],
    verdict: normalizeVerdict(perf.verdict),
  };
}

function normalizeOverallSummary(raw?: RawOverallSummary): OverallSummary | undefined {
  if (!raw) return undefined;
  return {
    executive_summary: raw.executive_summary ?? '',
    key_highlights: Array.isArray(raw.key_highlights) ? raw.key_highlights : [],
    recommendations: typeof raw.recommendations === 'string'
      ? [raw.recommendations]
      : Array.isArray(raw.recommendations)
      ? raw.recommendations
      : [],
    risk_factors: Array.isArray(raw.risk_factors) ? raw.risk_factors : [],
  };
}

function normalizeInsights(raw?: RawInsights): DocumentInsightsData | undefined {
  if (!raw) return undefined;
  return {
    overall_summary: normalizeOverallSummary(raw.overall_summary),
    revenue_commentary: normalizeCommentary(raw.revenue_commentary),
    ebitda_commentary: normalizeCommentary(raw.ebitda_commentary),
    asset_efficiency_commentary: normalizeCommentary(raw.asset_efficiency_commentary),
    industrial_performance_commentary: normalizeCommentary(raw.industrial_performance_commentary),
    financial_metrics: raw.financial_metrics,
    sentiment: raw.sentiment as DocumentInsightsData['sentiment'],
    extraction_confidence: normalizeConfidence(raw.extraction_confidence),
  };
}

function normalizeDocumentResult(raw?: RawDocumentResult): DocumentResult | undefined {
  if (!raw) return undefined;
  return {
    status: raw.status ?? 'failed',
    document_type: (raw.document_type ?? raw.metadata?.document_type ?? 'mda') as DocumentResult['document_type'],
    metadata: raw.metadata
      ? {
          pdf_url: raw.metadata.pdf_url,
          document_type: raw.metadata.document_type,
          fiscal_year: raw.metadata.fiscal_year ?? undefined,
          fiscal_quarter: raw.metadata.fiscal_quarter ?? undefined,
          source: raw.metadata.source,
          download_status: raw.metadata.download_status,
        }
      : undefined,
    insights: normalizeInsights(raw.insights),
  };
}

function normalizeResponse(raw: RawApiResponse): DocumentInsightsResponse {
  return {
    status: (raw.status ?? 'failed') as DocumentInsightsResponse['status'],
    company_name: raw.company_name ?? raw.company ?? raw.input_company_name ?? 'Unknown',
    combined_summary: raw.combined_summary,
    mda: normalizeDocumentResult(raw.mda),
    earnings: normalizeDocumentResult(raw.earnings),
  };
}

// ─── Cache config ─────────────────────────────────────────────────────────────

const QUERY_KEY = ['document-insights', 'analyze'] as const;
const STALE_TIME_MS = 1000 * 60 * 15; // 15 min — within this window, mounting
                                       // the tab again does NOT refetch
const GC_TIME_MS = 1000 * 60 * 60;     // 1 h — keep cache around longer than stale

const cacheKey = (companyName: string, country: string) =>
  `${companyName.trim().toLowerCase()}::${country.trim().toLowerCase()}`;

// ─── API call ─────────────────────────────────────────────────────────────────

async function postDocumentInsights(
  req: DocumentInsightsRequest,
): Promise<DocumentInsightsResponse> {
  const { data } = await apiClient.post<RawApiResponse>(
    '/analysis/documentInsights',
    req,
    // 10 min — cold paths download 2 PDFs (MD&A + earnings), parse them,
    // and run 2 sequential LLM calls. Warm cache hits return in <1s, but
    // a cold request with slow IR/NSE pages + body parsing + 2 LLM rounds
    // can legitimately take 4-6 minutes. 10 min gives headroom for tail
    // latency on production days when the LLM provider is contended.
    { timeout: 600000 },
  );
  return normalizeResponse(data);
}

// ─── Stage animation ──────────────────────────────────────────────────────────

const STAGE_DURATIONS_MS = [3000, 5000, 8000, 15000, 10000, 5000];

function useLoadingStages(isPending: boolean) {
  const [stageIndex, setStageIndex] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    if (!isPending) {
      clearTimers();
      setStageIndex(0);
      return;
    }

    clearTimers();
    setStageIndex(0);
    let elapsed = 0;
    LOADING_STAGES.forEach((_stage, i) => {
      if (i === 0) return;
      elapsed += STAGE_DURATIONS_MS[i - 1];
      const t = setTimeout(() => setStageIndex(i), elapsed);
      timersRef.current.push(t);
    });

    return clearTimers;
  }, [isPending]);

  return {
    stageIndex,
    currentStage: LOADING_STAGES[stageIndex],
  };
}

// ─── Public types ─────────────────────────────────────────────────────────────

export interface UseDocumentInsightsOptions {
  companyName: string | null | undefined;
  country: string;
  autoFetch?: boolean;
}

export interface UseDocumentInsightsResult {
  data: DocumentInsightsResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  stageIndex: number;
  currentStage: string;
  fetchedAt: number | null;
  fetch: () => void;
  refresh: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDocumentInsights({
  companyName,
  country,
  autoFetch = true,
}: UseDocumentInsightsOptions): UseDocumentInsightsResult {
  // Carries `force_refresh: true` from the user's Refresh click into the next
  // queryFn invocation. A ref (not state) avoids re-render churn between the
  // refetch trigger and the queryFn read.
  const forceRefreshRef = useRef(false);

  // useQuery handles both pain points natively:
  //   1. Concurrent fetches for the same queryKey are deduplicated, so React 18
  //      StrictMode's mount → unmount → remount cycle results in a SINGLE
  //      network call (the second mount subscribes to the existing in-flight
  //      promise rather than starting a new one).
  //   2. Subscribed components automatically re-render when the cache updates,
  //      so the panel switches from skeleton to data the moment the response
  //      lands — no manual subscription wiring needed.
  const queryResult = useQuery<DocumentInsightsResponse, Error>({
    queryKey: [...QUERY_KEY, cacheKey(companyName ?? '__none__', country)],
    queryFn: async () => {
      const force = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return postDocumentInsights({
        company_name: companyName!,
        country: country.toLowerCase(),
        force_refresh: force,
      });
    },
    enabled: !!companyName && autoFetch,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    refetchOnMount: false,       // already-cached data is shown instantly on remount
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const { stageIndex, currentStage } = useLoadingStages(queryResult.isFetching);

  const fetch = useCallback(() => {
    if (!companyName) return;
    forceRefreshRef.current = false;
    queryResult.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, queryResult.refetch]);

  const refresh = useCallback(() => {
    if (!companyName) return;
    forceRefreshRef.current = true;
    queryResult.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, queryResult.refetch]);

  const data = queryResult.data ?? null;

  return {
    data,
    isLoading: queryResult.isFetching && !data,
    isRefreshing: queryResult.isFetching && !!data,
    error: queryResult.error ? queryResult.error.message : null,
    stageIndex,
    currentStage,
    fetchedAt: queryResult.dataUpdatedAt || null,
    fetch,
    refresh,
  };
}
